import { FunctionComponent, memo } from "react";
import { SpinnerUntil } from "@edgetier/components";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Box, BoxContent, BoxHeader, BoxHeading, BoxSubheading } from "@edgetier/client-components";
import { IProps } from "./agents-state-chart.types";
import { useAgentStates } from "hooks-for/agents/use-agent-states";
import { useAgentCounts } from "hooks-for/agents/use-agent-counts";
import { AgentStateType, IAgentsCount } from "@edgetier/watchtower-types";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import BasicChartTooltip from "components-for/charts/basic-chart-tooltip";
import { useQueryParameters } from "@edgetier/client-components";
import { parseUrlArray } from "@edgetier/utilities";
import "./agents-state-chart.scss";

/**
 * Chart to display how many agents are in each state
 */
const AgentsStateChart: FunctionComponent<IProps> = () => {
    const { tagId } = useQueryParameters({ tagId: parseUrlArray });
    const { data: agentStates, isLoading: isAgentStatesLoading } = useAgentStates(
        {},
        { refetchInterval: LIVE_POLL_INTERVAL_MILLISECONDS }
    );
    const { data: agentsCounts, isLoading: isAgentsCountLoading } = useAgentCounts(
        { groupBy: "agent_state_id", agentStateType: [AgentStateType.Available, AgentStateType.Unavailable], tagId },
        { refetchInterval: LIVE_POLL_INTERVAL_MILLISECONDS }
    );

    return (
        <Box className="agents-state-chart">
            <BoxHeader>
                <BoxHeading>Agents State</BoxHeading>
                <BoxSubheading>Number of agents grouped by state</BoxSubheading>
            </BoxHeader>
            <BoxContent>
                <SpinnerUntil<IAgentsCount<string>[]>
                    data={agentsCounts}
                    isReady={!isAgentStatesLoading && !isAgentsCountLoading}
                >
                    {(chartData) => (
                        <ResponsiveContainer height="100%" width="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid />
                                <Bar dataKey="count" radius={5} />
                                <XAxis
                                    dataKey="group"
                                    tickFormatter={(value) => {
                                        const agentState = agentStates?.find((state) => state.agentStateId === value);
                                        return typeof agentState !== "undefined" ? agentState.agentState : "Unknown";
                                    }}
                                />
                                <YAxis tickFormatter={(value) => value} />
                                <Tooltip
                                    isAnimationActive={false}
                                    content={({ active, payload }) =>
                                        active &&
                                        Array.isArray(payload) &&
                                        typeof payload[0].value === "number" && (
                                            <BasicChartTooltip frequency={payload[0].payload.count} label="agents" />
                                        )
                                    }
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </SpinnerUntil>
            </BoxContent>
        </Box>
    );
};

export default memo(AgentsStateChart);
